import { toast } from 'sonner';

import { useUpdateAsset } from '@/hooks/useAssets';
import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { parseKeys } from '@/sections/SearchByType';
import { AssetStatus } from '@/types';
import { pluralize } from '@/utils/pluralize.util';

export interface UseAssetStatusHandlersParams {
  /** Current bulk selection state — used to decide per-item vs bulk path */
  selectionState?: BulkSelectionState;
  /**
   * Called with the requested status when shouldUseBulk(selectionState) is true.
   * The caller (index.tsx) shows a BulkConfirmationDialog and submits the bulk job.
   */
  onBulkRequested?: (status: AssetStatus) => void;
}

/**
 * Custom hook for asset status update handlers
 * Handles freeze, unfreeze, and delete operations
 *
 * When selectionState indicates a bulk operation (mode='all' or manual > BULK_THRESHOLD),
 * the handler delegates to onBulkRequested instead of per-item mutations.
 *
 * @returns Object containing handleUpdateStatus function
 */
export const useAssetStatusHandlers = ({
  selectionState,
  onBulkRequested,
}: UseAssetStatusHandlersParams = {}) => {
  const { mutateAsync: updateAsset } = useUpdateAsset();

  /**
   * Updates the status of multiple assets
   * Executes updates in parallel with graceful error handling
   *
   * Uses Promise.allSettled to handle partial failures and provide
   * accurate feedback to users about permission-related errors.
   *
   * @param assets - Assets to update
   * @param status - New status to apply
   */
  const handleUpdateStatus = async (
    assets: { key: string }[],
    status: AssetStatus
  ) => {
    // Delegate to bulk endpoint when selection warrants it
    if (selectionState && shouldUseBulk(selectionState) && onBulkRequested) {
      onBulkRequested(status);
      return;
    }

    // Execute all updates in parallel, allowing partial success
    const promises = assets.map(asset =>
      updateAsset({
        key: asset.key,
        name: parseKeys.assetKey(asset.key).identifier,
        status,
        showSnackbar: false, // Suppress individual toasts, show bulk summary
      })
    );

    const results = await Promise.allSettled(promises);

    const succeeded = results.filter(r => r.status === 'fulfilled').length;
    const failed = results.filter(r => r.status === 'rejected').length;

    // Check for permission-related failures (MSP required for unfreeze)
    const failedBecauseOfPermission = results.filter(
      r =>
        r.status === 'rejected' &&
        r.reason?.response?.data?.includes('Please contact MSP')
    );

    // Always invalidate cache if any operations succeeded
    if (succeeded > 0) {
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('asset'),
      });
    }

    // Show appropriate feedback based on results
    if (succeeded > 0 && failed === 0) {
      toast.success(
        `Status updated for ${succeeded} ${pluralize(succeeded, 'asset')}`
      );
    } else if (succeeded > 0 && failed > 0) {
      toast.warning(
        `Status updated for ${succeeded} ${pluralize(succeeded, 'asset')}, ` +
          `but failed for ${failed} ${pluralize(failed, 'asset')}`
      );
    }

    // Show specific error message for permission failures
    if (failedBecauseOfPermission.length > 0) {
      toast.error(
        'Only Praetorian users can unfreeze assets. Please contact your MSP.'
      );
    } else if (failed > 0 && succeeded === 0) {
      toast.error(`Failed to update ${failed} ${pluralize(failed, 'asset')}`);
    }
  };

  return { handleUpdateStatus };
};
